import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { FaBell, FaCheckCircle, FaInfoCircle, FaExclamationCircle } from "react-icons/fa";
import "./Navbar.scss";

const filterMenuItems = (items, allowedPages) =>
  items.filter(item => item.enabled && (item.skipFilter || item.isExternal || allowedPages.includes(item.key)));

const trackMainMenuClick = (buttonText) => {
  if (typeof window.ym !== "function") return;

  window.ym(112273943, "reachGoal", "main_menu_click", {
    main_menu_click: buttonText,
  });
};

const formatCreatedAt = (isoDate) => {
  if (!isoDate) return "";
  try {
    return new Date(isoDate).toLocaleString("ru-RU");
  } catch {
    return isoDate;
  }
};

const getTypeMeta = (type) => {
  if (type === "completed") return { label: "Завершено", className: "success", icon: <FaCheckCircle /> };
  if (type === "error" || type === "failed") {
    return { label: "Ошибка", className: "error", icon: <FaExclamationCircle /> };
  }
  return { label: "Событие", className: "info", icon: <FaInfoCircle /> };
};

function Navbar({
  user,
  setUser,
  allowedPages = [],
  notifications = [],
  unreadCount = 0,
  notificationsLoading = false,
  notificationsError = null,
  markNotificationsSeen = null,
}) {
  const hasAdminMenu = user?.role === "admin";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const menuItems = [
    { path: "/", key: "Collect", label: "Сбор данных", enabled: hasAdminMenu },
    { path: "/source-data", key: "SourceData", label: "Исходные данные", enabled: true },
    { path: "/export-data", key: "ExportData", label: "Экспорт данных", enabled: true },
    { path: "/posts-search", key: "PostsSearch", label: "Поиск по постам", enabled: true },
    { path: "/admin", key: "Admin", label: "Администрирование", enabled: hasAdminMenu },
  ];
  

  const navigate = useNavigate();
  const navbarRef = useRef(null);

  const filteredMenuItems = useMemo(
    () => filterMenuItems(menuItems, allowedPages),
    [allowedPages, hasAdminMenu]
  );

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("deniedPages");
    setUser(null);
    navigate("/login");
  };

  const handleMenuItemClick = (buttonText) => {
    setIsMenuOpen(false);
    trackMainMenuClick(buttonText);
  };

  useEffect(() => {
    if (!isNotificationsOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsNotificationsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isNotificationsOpen]);

  const sortedNotifications = useMemo(
    () => [...notifications].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [notifications]
  );
  const unreadNotifications = useMemo(
    () => sortedNotifications.filter((item) => item.read === false),
    [sortedNotifications]
  );
  const readNotifications = useMemo(
    () => sortedNotifications.filter((item) => item.read !== false),
    [sortedNotifications]
  );
  const hasNotifications = sortedNotifications.length > 0;
  const showLoadingState = notificationsLoading && !hasNotifications;
  const showErrorState = notificationsError && !hasNotifications;
  const showEmptyState = !notificationsLoading && !notificationsError && !hasNotifications;

  const renderNotification = (item) => {
    const typeMeta = getTypeMeta(item.type);
    return (
      <div
        key={item.notification_id}
        onClick={() =>
          item.read === false &&
          markNotificationsSeen &&
          markNotificationsSeen(item.notification_id)
        }
        className={`notifications-item notifications-item--${typeMeta.className} ${
          item.read === false ? "notifications-item--unread" : "notifications-item--read"
        }`}
      >
        <div className="notifications-item__top">
          <span className={`notifications-item__type notifications-item__type--${typeMeta.className}`}>
            {typeMeta.icon}
            {typeMeta.label}
          </span>
          <span className="notifications-item__date">{formatCreatedAt(item.created_at)}</span>
        </div>

        <div className="notifications-item__message">{item.message}</div>

        <div className="notifications-item__meta">
          {item.task_id ? <span>Task: {item.task_id}</span> : <span>Task: -</span>}
          {item.session_id ? <span>Session: {item.session_id}</span> : <span>Session: -</span>}
        </div>
      </div>
    );
  };

  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="navbar-content">
        <Link to="/" className="navbar-brand" aria-label="Socio.Parser">
          <img src="/logo512K.png" alt="" aria-hidden="true" />
          <span className="navbar-brand__name">Socio.Parser</span>
        </Link>

        <div className="navbar-tabs-wrapper">
          <button
            type="button"
            className={`navbar-burger${isMenuOpen ? " navbar-burger--open" : ""}`}
            onClick={() => setIsMenuOpen(prev => !prev)}
            aria-label="Переключить меню навигации"
          >
            <span />
            <span />
            <span />
          </button>

          <div className={`nav-links${isMenuOpen ? " nav-links--open" : ""}`}>
            {filteredMenuItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}
                onClick={() => handleMenuItemClick(item.label)}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>

        <div className="navbar-actions">
          <button
            type="button"
            className="notifications-bell-btn"
            onClick={() => setIsNotificationsOpen((value) => !value)}
            aria-label="Открыть уведомления"
          >
            <FaBell />
            {unreadCount > 0 && <span className="notifications-bell-btn__badge">{unreadCount}</span>}
          </button>

          <button className="logout-btn" onClick={handleLogout}>
            Выйти
          </button>
        </div>
      </div>

      {isNotificationsOpen && (
        <div className="notifications-modal-overlay" onClick={() => setIsNotificationsOpen(false)}>
          <div className="notifications-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notifications-modal__header">
              <div>
                <h3>Уведомления по задачам</h3>
                <p>Создание, запуск, завершение и ошибки задач</p>
              </div>
              <button
                type="button"
                className="notifications-modal__close"
                onClick={() => setIsNotificationsOpen(false)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className="notifications-modal__content">
              {showLoadingState && (
                <div className="notifications-modal__state">Загружаем уведомления...</div>
              )}
              {showErrorState && (
                <div className="notifications-modal__state notifications-modal__state--error">
                  {notificationsError}
                </div>
              )}
              {showEmptyState && (
                <div className="notifications-modal__state">Пока нет уведомлений</div>
              )}

              {hasNotifications && (
                <>
                  <div className="notifications-section">
                    <div className="notifications-section__title">
                      Непрочитанные <span>{unreadNotifications.length}</span>
                    </div>
                    {unreadNotifications.length > 0 ? (
                      unreadNotifications.map(renderNotification)
                    ) : (
                      <div className="notifications-section__empty">Новых уведомлений нет</div>
                    )}
                  </div>

                  <div className="notifications-section">
                    <div className="notifications-section__title">
                      Прочитанные <span>{readNotifications.length}</span>
                    </div>
                    {readNotifications.length > 0 ? (
                      readNotifications.map(renderNotification)
                    ) : (
                      <div className="notifications-section__empty">Прочитанных уведомлений пока нет</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
